<template>
  <div class="tenant-setting-page">
    <div class="page-header">
      <div>
        <p class="eyebrow">TENANT SETTINGS</p>
        <h1>自社設定</h1>
        <p>会社情報、勤怠計算ルール、休日カレンダー、休憩、Slack通知を現在の会社単位で管理します。</p>
      </div>
      <!--
        **ここに「設定を編集」は置かない。**
        押すと全項目の1枚に飛ぶので、どれを直したくても同じ場所へ着く。
        直したい項目のカードにある「変更」から入る。
      -->
    </div>

    <CAlert v-if="errorMessage" color="danger" close-button @update:show="errorMessage = ''">
      {{ errorMessage }}
    </CAlert>

    <div v-if="loading" class="setting-loading">
      <CSpinner color="primary"/>
      <span>自社設定を読み込んでいます。</span>
    </div>

    <template v-else>
      <CCard class="setting-card">
        <CCardHeader>
          <div class="section-heading">
            <span class="section-icon"><CIcon name="cil-building"/></span>
            <div>
              <strong>会社情報</strong>
              <p>契約企業としての表示および連絡先として使用する情報です。</p>
            </div>
            <SettingSectionAction :to="{ name: '自社設定の会社情報' }"/>
          </div>
        </CCardHeader>
        <CCardBody>
          <dl class="setting-summary-grid">
            <div><dt>会社名</dt><dd>{{ display(settings.company_name) }}</dd></div>
            <div><dt>会社メール</dt><dd>{{ display(settings.company_email) }}</dd></div>
            <div><dt>電話番号</dt><dd>{{ display(settings.telephone) }}</dd></div>
          </dl>
        </CCardBody>
      </CCard>

      <!--
        請求書記載情報（住所・インボイス登録番号・振込先など）。
        取引先向けの請求書（請求書管理）に載る、自社の情報。

        **請求書を出せる契約の会社にだけ出す。**出せない会社に出すと、
        入れても何にも使われない欄になる。契約を確かめられなかったときも出さない。

        保存の口は専用（上の会社情報とは別）。上の会社情報を保存しても、ここの値は
        変わらない。
      -->
      <CCard v-if="invoiceIssuerVisible" class="setting-card">
        <CCardHeader>
          <div class="section-heading">
            <span class="section-icon"><CIcon name="cil-layers"/></span>
            <div>
              <strong>{{ invoiceIssuerTitle }}</strong>
              <p>{{ invoiceIssuerSummary }}</p>
            </div>
            <SettingSectionAction :to="{ name: '自社設定の請求書記載情報' }"/>
          </div>
        </CCardHeader>
        <CCardBody>
          <CAlert v-if="invoiceIssuerError" color="danger" class="mb-0">{{ invoiceIssuerError }}</CAlert>
          <dl v-else class="setting-summary-grid setting-summary-grid--wide">
            <div v-for="field in invoiceIssuerFields" :key="field.key">
              <dt>{{ field.label }}</dt><dd>{{ display(invoiceIssuer[field.key]) }}</dd>
            </div>
            <div><dt>{{ invoiceIssuerSealLabel }}</dt><dd>{{ invoiceIssuer.has_seal ? '登録済み' : '未登録' }}</dd></div>
          </dl>
        </CCardBody>
      </CCard>

      <!--
        ログイン方法だけは、ここで直接編集できる。**「設定を編集」の画面へは
        置かない。**あちらは一括で保存する形なので、締め出しが起きうるフラグが
        勤怠のルールに紛れて一緒に保存されてしまう。
      -->
      <AuthPolicySettings compact/>

      <!--
        管理者の2要素も、ここで直接編集する。**ログイン方法とは別の
        カードにする。**同じカードにまとめると、片方を変えたつもりで
        もう片方も保存されてしまう（サーバ側も口を分けてある）。
      -->
      <AdminMfaPolicySettings compact/>

      <CCard class="setting-card">
        <CCardHeader>
          <div class="section-heading">
            <span class="section-icon"><CIcon name="cil-clock"/></span>
            <div>
              <strong>勤怠計算・打刻</strong>
              <p>勤務表および打刻計算に適用される会社共通ルールです。</p>
            </div>
            <SettingSectionAction :to="{ name: '自社設定の勤怠計算' }"/>
          </div>
        </CCardHeader>
        <CCardBody>
          <CAlert color="info">
            未設定時は、打刻猶予なし・時間外1分単位・規定勤務8時間・規定休憩1時間を使用します。
            設定済みの値はそのまま勤務表計算へ反映します。
          </CAlert>
          <dl class="setting-summary-grid setting-summary-grid--wide">
            <div><dt>定時前打刻猶予</dt><dd>{{ timeLabel(settings.pre_buffer) }}</dd></div>
            <div><dt>定時後打刻猶予</dt><dd>{{ timeLabel(settings.post_buffer) }}</dd></div>
            <div><dt>時間外の切り上げ単位</dt><dd>{{ timeLabel(settings.work_time_unit) }}</dd></div>
            <div><dt>規定勤務時間</dt><dd>{{ timeLabel(settings.work_basic) }}</dd></div>
            <div><dt>規定休憩時間</dt><dd>{{ timeLabel(settings.break_basic) }}</dd></div>
            <div><dt>定時後猶予を休憩扱い</dt><dd>{{ yesNo(settings.post_work_break_flg) }}</dd></div>
            <div><dt>プロジェクト休憩を自動反映</dt><dd>{{ yesNo(settings.use_project_break_flg) }}</dd></div>
            <div><dt>半日単位の休暇</dt><dd>{{ yesNo(settings.half_day_leave_flg !== false) }}</dd></div>
            <div><dt>打刻時の位置情報記録</dt><dd>{{ yesNo(settings.stamping_location_flg === true) }}</dd></div>
            <div><dt>指定エリア外の打刻制限</dt><dd>{{ yesNo(settings.stamping_location_flg === true && settings.stamping_location_restrict_flg === true) }}</dd></div>
            <div>
              <dt>代休の期限</dt>
              <dd>{{ substituteExpiryText }}</dd>
            </div>
          </dl>
        </CCardBody>
      </CCard>

      <!--
        36協定（時間外労働の上限）。**施行日つきの履歴で持つ。**

        36協定は1年ごとに結び直す。1組だけ持つと、今年の協定を入れた
        瞬間に去年の月まで今年の数値で判定され直す——当時は適法だった
        月が違反として出てくるし、緩めた会社では去年の違反が消える。
        だからここに出すのは「いま効いている1本」と、その履歴。

        **「実際に当てはめる上限」はサーバが計算して返す。**1年単位の
        変形労働時間制にチェックが入ると法定の原則値が42時間/320時間へ
        下がり、特別条項が無ければ年の天井は原則の限度時間のままになる。
        この読み替えを画面でも書くと、どちらかを直したときに表示と判定が
        食い違う。**どれが「いま効いている」かもサーバが決める。**
      -->
      <!--
        **労務法令の判定を出していないあいだは、欄ごと出さない。**

        入れてもらっても判定に使わないので、入れた人には何も起きない。
        しかも入力の形（1年の起算日の扱いなど）はこれから変わるので、
        いま入れた内容をあとで入れ直してもらうことになる。
        back も書く口を断る。
      -->
      <CCard v-if="legalComplianceEnabled" class="setting-card">
        <CCardHeader>
          <div class="section-heading">
            <span class="section-icon"><CIcon name="cil-warning"/></span>
            <div>
              <strong>時間外労働の上限（36協定）</strong>
              <p>労働基準法第36条に基づく上限です。未設定の場合は原則的な法定上限（月45時間・年360時間・特別条項なし）が適用されます。</p>
            </div>
            <!--
              **他のカードと同じ位置に置く。**ここに無かったせいで、
              入れる場所が分からないという声が実際に出た。

              行き先は節ごとの編集画面ではなく「設定更新」。
              36協定は専用の保存口を使うので節から外してあり、
              いまはこの画面にしか入力欄が無い。

              文言も「変更」ではなく「協定を登録・変更」にする——
              まだ1本も登録していない会社にとって、
              「変更」は自分のすることに見えない。
            -->
            <SettingSectionAction
              :to="{ name: '設定更新' }"
              label="協定を登録・変更"
            />
          </div>
        </CCardHeader>
        <CCardBody>
          <!--
            いま判定できているのか。**「問題なし」と同じ見た目にしない。**
            協定が無い・切れている・内容が未登録の会社を、上限に触れていない
            会社と同じ顔で出すと、見ていないことが見えなくなる。
          -->
          <div class="agreement-status" :class="agreementStatusClasses" role="status">
            <span class="agreement-status__badge">{{ agreementStatusNotice.badge }}</span>
            <div class="agreement-status__body">
              <strong>{{ agreementStatusNotice.headline }}</strong>
              <small>{{ agreementStatusNotice.detail }}</small>
              <small class="agreement-status__action">{{ agreementStatusNotice.action }}</small>
            </div>
          </div>
          <p class="setting-note">{{ agreementPeriodLabel }}</p>
          <dl class="setting-summary-grid setting-summary-grid--wide">
            <div><dt>適用開始日</dt><dd>{{ effectiveFromLabel }}</dd></div>
            <div><dt>有効期間の終わり</dt><dd>{{ validToLabel }}</dd></div>
            <div><dt>対象期間の起算日</dt><dd>{{ annualStartLabel }}</dd></div>
            <div><dt>対象期間の起算月</dt><dd>{{ startMonthLabel }}</dd></div>
            <div><dt>1年単位の変形労働時間制</dt><dd>{{ yesNo(settings.overtime_agreement_variable_year_flg) }}</dd></div>
            <div><dt>特別条項</dt><dd>{{ settings.overtime_agreement_special_clause_flg ? 'あり' : 'なし' }}</dd></div>
          </dl>
          <dl class="setting-summary-grid setting-summary-grid--wide">
            <div>
              <dt>月の限度時間</dt>
              <dd>{{ limitLabel(settings.overtime_agreement_monthly_limit_minutes, settings.overtime_effective_monthly_limit_minutes) }}</dd>
            </div>
            <div>
              <dt>年の限度時間</dt>
              <dd>{{ limitLabel(settings.overtime_agreement_yearly_limit_minutes, settings.overtime_effective_yearly_limit_minutes) }}</dd>
            </div>
            <div>
              <dt>年の天井</dt>
              <dd>{{ hoursLabel(settings.overtime_effective_yearly_ceiling_minutes) }}</dd>
            </div>
            <div>
              <dt>限度時間を超えられる回数</dt>
              <dd>{{ exceedLabel }}</dd>
            </div>
          </dl>
          <!--
            協定の履歴。**過去の月は、その時点で効いていた協定で判定される。**
            ここが無いと、「去年の8月は45時間で見られたはずなのに、画面には
            42時間と出ている」という食い違いを説明できない。

            どれが効いているかは current_flg でサーバが決める。画面で
            「今日以前で最新」を計算すると、判定が読む1本とずれうる。
          -->
          <div v-if="agreementHistory.length" class="setting-agreement-history">
            <h6>協定の履歴（新しい順）</h6>
            <table>
              <thead>
                <tr>
                  <th>適用開始日</th>
                  <th>有効期間の終わり</th>
                  <th>起算月</th>
                  <th>月の限度時間</th>
                  <th>年の限度時間</th>
                  <th>特別条項</th>
                  <th>内容</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in agreementHistory" :key="row.valid_from">
                  <td>
                    {{ dayLabel(row.valid_from) }}
                    <CBadge v-if="row.current_flg" color="info">いま効いている</CBadge>
                  </td>
                  <td>{{ dayLabel(row.valid_to) }}</td>
                  <td>{{ row.overtime_agreement_start_month }}月</td>
                  <td>{{ hoursLabel(row.overtime_agreement_monthly_limit_minutes) }}</td>
                  <td>{{ hoursLabel(row.overtime_agreement_yearly_limit_minutes) }}</td>
                  <td>{{ row.overtime_agreement_special_clause_flg ? 'あり' : 'なし' }}</td>
                  <td>{{ row.confirmed_flg ? '登録済み' : '未登録' }}</td>
                </tr>
              </tbody>
            </table>
            <p class="setting-note">
              過去の月は、<strong>その月に効いていた協定</strong>で判定します。
              協定を結び直したときは、新しい適用開始日で1本足してください。
              前の行を書き換えると、当時の判定まで変わります。
            </p>
          </div>

          <CAlert color="warning">
            <strong>法令により、特別条項の有無にかかわらず守るべき上限があります（時間外労働と休日労働の合計時間など）。</strong>
            これは協定で緩和できないため、設定項目にはありません。
          </CAlert>
        </CCardBody>
      </CCard>

      <CCard class="setting-card">
        <CCardHeader>
          <div class="section-heading">
            <span class="section-icon"><CIcon name="cil-sun"/></span>
            <div>
              <strong>就業時間</strong>
              <p>朝および夕方のSlack通知は、設定された時間を基準に送信されます。</p>
            </div>
            <SettingSectionAction :to="{ name: '自社設定の就業時間' }"/>
          </div>
        </CCardHeader>
        <CCardBody>
          <dl class="setting-summary-grid setting-summary-grid--wide">
            <div><dt>始業</dt><dd>{{ display(settings.business_start_time) }}</dd></div>
            <div><dt>終業</dt><dd>{{ display(settings.business_end_time) }}</dd></div>
          </dl>
          <dl class="setting-summary-grid setting-summary-grid--wide">
            <div>
              <dt>当日休暇予定の通知時刻</dt>
              <dd>{{ leadLabel(settings.daily_schedule_notification_lead_minutes, '始業', settings.daily_schedule_notification_time) }}</dd>
            </div>
            <div>
              <dt>承認待ちのお知らせを送る時間</dt>
              <dd>{{ leadLabel(settings.pending_approval_notification_lead_minutes, '始業', settings.pending_approval_notification_time) }}</dd>
            </div>
            <div>
              <dt>翌日の未承認休暇の通知時刻</dt>
              <dd>{{ leadLabel(settings.pending_leave_reminder_lead_minutes, '終業', settings.pending_leave_reminder_time) }}</dd>
            </div>
          </dl>
        </CCardBody>
      </CCard>

      <HolidayCalendarSettings compact/>

      <CCard class="setting-card">
        <CCardHeader>
          <div class="section-heading">
            <span class="section-icon"><CIcon name="cil-bell"/></span>
            <div>
              <strong>Slack連携・通知</strong>
              <p>通知をオンにした項目のみ、指定されたチャンネルへ送信します。</p>
            </div>
            <SettingSectionAction :to="{ name: '自社設定の通知' }"/>
          </div>
        </CCardHeader>
        <CCardBody>
          <CAlert v-if="!settings.slack_feature_enabled" color="warning">
            現在のご契約や設定では、Slack通知をご利用いただけません。利用をご希望の場合は、社内の管理者にご相談ください。
          </CAlert>

          <div class="slack-status-row">
            <div>
              <span class="status-label">Slackトークン</span>
              <strong>{{ settings.slack_token_configured ? '設定済み' : '未設定' }}</strong>
            </div>
            <CBadge :color="settings.slack_token_configured ? 'success' : 'secondary'">
              {{ settings.slack_token_configured ? '接続情報あり' : '接続情報なし' }}
            </CBadge>
          </div>

          <div class="notification-grid">
            <article class="notification-card">
              <div class="notification-card__header">
                <div>
                  <h3>申請状況の即時通知</h3>
                  <p>申請、申請取消、差戻しなどのタイミングで通知します。</p>
                </div>
                <CBadge :color="toggleColor(settings.slack_notification_flg)">
                  {{ onOff(settings.slack_notification_flg) }}
                </CBadge>
              </div>
              <dl>
                <div><dt>通知先チャンネル</dt><dd>{{ display(settings.slack_channel) }}</dd></div>
              </dl>
            </article>

            <article class="notification-card">
              <div class="notification-card__header">
                <div>
                  <h3>承認待ちリマインド</h3>
                  <p>未承認データが残っている場合、会社営業日の朝に通知します。</p>
                </div>
                <CBadge :color="toggleColor(settings.pending_approval_notification_flg)">
                  {{ onOff(settings.pending_approval_notification_flg) }}
                </CBadge>
              </div>
              <dl>
                <div><dt>通知先チャンネル</dt><dd>{{ display(settings.pending_approval_slack_channel) }}</dd></div>
                <div><dt>実行時間</dt><dd>{{ display(settings.pending_approval_notification_time) }}（1日1回）</dd></div>
              </dl>
            </article>

            <article class="notification-card">
              <div class="notification-card__header">
                <div>
                  <h3>管理部門向け朝通知</h3>
                  <p>当日の承認済み休暇予定だけを {{ display(settings.daily_schedule_notification_time) }} に通知します。</p>
                </div>
                <CBadge :color="toggleColor(settings.management_daily_notification_flg)">
                  {{ onOff(settings.management_daily_notification_flg) }}
                </CBadge>
              </div>
              <dl>
                <div><dt>通知先チャンネル</dt><dd>{{ display(settings.management_daily_slack_channel) }}</dd></div>
                <div><dt>実行時間</dt><dd>{{ display(settings.daily_schedule_notification_time) }}</dd></div>
              </dl>
            </article>

            <!--
              36協定アラート。**月に1回だけ。**

              会社が受け取るかどうかを選べる知らせではないので、既定でオン。
              通知先は2段で、専用のチャンネルが空なら管理部門向けへ送る。
              文面に氏名は出さず、人数の内訳と画面への案内だけ。

              **届かないときは必ず出す。**送り先が1つも無い会社にも、
              算定期日を消した会社にも、通知は1通も出ない。それでも
              エラーは1行も出ないので、ここに出さないと黙って消える。
            -->
            <!--
              **判定を出していないあいだは、この知らせの設定も出さない。**
              通知は1通も送らないので、通知先を決めても何も起きない。
              直しても何も起きない設定を促すと、次に本当に届かなく
              なったときにはもう読まれない。
            -->
            <article v-if="legalComplianceEnabled" class="notification-card">
              <div class="notification-card__header">
                <div>
                  <h3>36協定アラート</h3>
                  <p>時間外・休日労働時間の算定期日に、上限に達している対象者の人数を通知します。</p>
                </div>
                <CBadge :color="toggleColor(settings.overtime_alert_notification_flg)">
                  {{ onOff(settings.overtime_alert_notification_flg) }}
                </CBadge>
              </div>
              <div
                v-if="overtimeAlertNotice"
                class="delivery-notice"
                :class="'delivery-notice--' + overtimeAlertNotice.tone"
                role="status"
              >
                <strong>{{ overtimeAlertNotice.headline }}</strong>
                <small>{{ overtimeAlertNotice.detail }}</small>
              </div>
              <dl>
                <div><dt>通知先チャンネル</dt><dd>{{ display(overtimeAlertChannelLabel) }}</dd></div>
                <div><dt>算定期日</dt><dd>{{ display(overtimeCalculationDueLabel) }}</dd></div>
                <div><dt>実行時間</dt><dd>{{ display(settings.business_start_time) }}（月1回）</dd></div>
              </dl>
            </article>
          </div>

          <PendingApprovalNotificationStatus/>
        </CCardBody>
      </CCard>

      <CCard class="settings-card">
        <CCardHeader>
          <div class="section-heading">
            <span class="section-icon"><CIcon name="cil-calendar"/></span>
            <div>
              <strong>ダッシュボードの社内カレンダー</strong>
              <p>未設定の場合、ダッシュボードにカレンダーは表示されません。</p>
            </div>
          </div>
        </CCardHeader>
        <CCardBody>
          <div v-if="settings.dashboard_calendar_embed_url" class="calendar-set">
            <!--
              **「設定済み」は状態で、値ではない。**続けて書くと
              「設定済みhttps://…」と読めて、値の一部に見える。
            -->
            <span class="calendar-set__badge">設定済み</span>
            <span class="calendar-set__url">{{ settings.dashboard_calendar_embed_url }}</span>
          </div>
          <p v-else class="calendar-unset">
            未設定です。ダッシュボードにカレンダーは表示されていません。
          </p>
        </CCardBody>
      </CCard>

      <CCard class="settings-card">
        <CCardHeader>
          <div class="section-heading">
            <span class="section-icon"><CIcon name="cil-paper-plane"/></span>
            <div>
              <strong>提出物の締め</strong>
              <p>ダッシュボードの「提出ガイド」に表示されます。未設定の場合、締め切り期限は表示されません。</p>
            </div>
            <SettingSectionAction :to="{ name: '自社設定の表示' }"/>
          </div>
        </CCardHeader>
        <CCardBody>
          <div v-if="submissionDueLabel" class="calendar-set">
            <span class="calendar-set__badge">{{ submissionDueLabel }}</span>
            <span v-if="settings.submission_due_items" class="calendar-set__url">{{ settings.submission_due_items }}</span>
          </div>
          <p v-else class="calendar-unset">
            未設定です。提出ガイドに期限は表示されていません。
          </p>
        </CCardBody>
      </CCard>

      <PaidLeaveGrantRules compact/>

      <!--
        他社からの乗り換え。**設定の一番下に置く。**
        ふだんは使わないが、乗り換えてくる会社は初日にここだけを探す。
      -->
      <CCard class="settings-card">
        <CCardHeader>
          <div class="section-heading">
            <span class="section-icon"><CIcon name="cil-cloud-upload"/></span>
            <div>
              <strong>データの取り込み</strong>
              <p>他社からの乗り換えで、部署などをCSVからまとめて登録します。ひな形はこの画面から受け取れます。</p>
            </div>
            <SettingSectionAction :to="{ name: 'データの取り込み' }" label="開く"/>
          </div>
        </CCardHeader>
      </CCard>

      <!--
        **ふだんはここを使わない。**各カードの「変更」から入る。
        36協定はまだこの画面にしか無いので、入口は残す。
      -->
      <div class="bottom-action">
        <router-link :to="{ name: '設定更新' }">
          <CButton color="secondary" variant="outline">
            全項目をまとめて編集
          </CButton>
        </router-link>
      </div>
    </template>
  </div>
</template>

<script>
import api from '@/services/companySettings'
import AuthPolicySettings from './AuthPolicySettings'
import AdminMfaPolicySettings from './AdminMfaPolicySettings'
import HolidayCalendarSettings from './HolidayCalendarSettings'
import PaidLeaveGrantRules from './PaidLeaveGrantRules'
import PendingApprovalNotificationStatus from './PendingApprovalNotificationStatus'
import SettingSectionAction from './SettingSectionAction.vue'
import { getCachedAccessContext } from '@/services/accessContext'
import {
  INVOICE_ISSUER_TEXT_FIELDS,
  INVOICE_ISSUER_SEAL_LABEL
} from './invoiceIssuerFields'
import {
  INVOICE_ISSUER_TITLE,
  INVOICE_ISSUER_SUMMARY
} from './invoiceIssuerTexts'
import { createAttendanceSettingDefaults } from '@/utils/attendanceSettings'
import { overtimeAlertDeliveryNotice } from '@/utils/overtimeLimitStatus'
import {
  overtimeAgreementStatusClasses,
  overtimeAgreementStatusNotice
} from '@/utils/overtimeAgreementStatus'

const emptySettings = () => ({
  company_name: '',
  company_email: '',
  telephone: '',
  /* 提出物の締め。**空は「決めていない」。** */
  submission_due_kind: '',
  submission_due_day: '',
  submission_due_items: '',
  ...createAttendanceSettingDefaults(),
  slack_notification_flg: false,
  slack_token_configured: false,
  slack_channel: '',
  pending_approval_notification_flg: false,
  pending_approval_slack_channel: '',
  management_daily_notification_flg: false,
  management_daily_slack_channel: '',
  /* 36協定アラート。**空は「決めていない」。** */
  overtime_alert_notification_flg: false,
  overtime_alert_slack_channel: '',
  overtime_calculation_due_kind: '',
  overtime_calculation_due_day: '',
  /*
   * 届くかどうかはサーバが決めて返す。**画面で数え直さない。**
   * 空を「届いている」に読み替えないので、既定は空のまま。
   */
  overtime_alert_delivery_blocker: '',
  overtime_alert_uses_management_channel: false,
  /*
   * 送信そのものに失敗した対象月（`YYYY-MM`）。**失敗していなければ空。**
   * 設定がそろっていても送れないことがあるので、blocker とは別に持つ。
   */
  overtime_alert_last_failed_month: '',
  slack_feature_enabled: false,
  business_start_time: '09:00',
  business_end_time: '18:00',
  daily_schedule_notification_lead_minutes: 30,
  pending_approval_notification_lead_minutes: 0,
  pending_leave_reminder_lead_minutes: 30,
  // 実際に届く時刻はサーバが計算して返す。画面では引き算をしない。
  // ここで同じ計算を書くと、片方を直したときに表示と実際が食い違う。
  daily_schedule_notification_time: '',
  pending_approval_notification_time: '',
  pending_leave_reminder_time: '',
  /*
   * 36協定。**時間ではなく分。**下の overtime_effective_* は
   * サーバが計算して返す「実際に当てはめる上限」で、保存はしない。
   * 画面で読み替えを書くと、表示と判定が食い違う。
   */
  overtime_agreement_start_month: 4,
  overtime_agreement_monthly_limit_minutes: 2700,
  overtime_agreement_yearly_limit_minutes: 21600,
  overtime_agreement_variable_year_flg: false,
  overtime_agreement_special_clause_flg: false,
  overtime_agreement_special_yearly_limit_minutes: 43200,
  overtime_agreement_exceed_month_count: 6,
  overtime_effective_monthly_limit_minutes: 2700,
  overtime_effective_yearly_limit_minutes: 21600,
  overtime_effective_yearly_ceiling_minutes: 21600,
  overtime_effective_exceed_month_count: 0,
  /*
   * いま効いている協定の適用開始日と、協定の履歴。
   * **空は「協定として記録されていない」。**法定の原則値で扱っている、
   * という意味で、「1900年から効いている協定が1本ある」とは別のこと。
   */
  overtime_agreement_valid_from: '',
  overtime_agreement_valid_to: '',
  overtime_agreement_annual_start_date: '',
  overtime_agreement_confirmed_flg: false,
  /*
   * 判定できているか。**サーバが決めて返す。**画面で数え直さない。
   */
  /* 労務法令の判定を出してよい段階か。**既定は出さない側。** */
  legal_compliance_enabled: false,
  overtime_agreement_status: '',
  overtime_agreement_status_message: '',
  overtime_agreement_history: []
})

export default {
  name: 'TenantSetting',
  components: {
    SettingSectionAction,
    AuthPolicySettings,
    AdminMfaPolicySettings,
    HolidayCalendarSettings,
    PaidLeaveGrantRules,
    PendingApprovalNotificationStatus
  },
  data () {
    return {
      loading: false,
      errorMessage: '',
      settings: emptySettings(),
      /*
       * 請求書記載情報。**専用の口で読む**（settings には混ぜない。
       * 混ぜると、全項目を送り直す保存に乗って一緒に飛ぶ）。
       * null は「まだ読んでいない・出さない」。
       */
      invoiceIssuer: null,
      invoiceIssuerError: '',
      invoiceIssuerTitle: INVOICE_ISSUER_TITLE,
      invoiceIssuerSummary: INVOICE_ISSUER_SUMMARY,
      invoiceIssuerFields: INVOICE_ISSUER_TEXT_FIELDS,
      invoiceIssuerSealLabel: INVOICE_ISSUER_SEAL_LABEL
    }
  },
  computed: {
    /** 請求書記載情報のカードを出すか。読めた（か、読もうとして失敗した）ときだけ。 */
    invoiceIssuerVisible () {
      return this.invoiceIssuer !== null
    },
    /**
     * 労務法令の判定を出してよい段階か。**サーバの応答から読む。**
     *
     * <p>front は設定を直接読まない。<b>既定は出さない側</b>——古いサーバや
     * 取得の失敗で欄が無いときに出す側へ倒すと、入れても判定に使われない
     * 欄が並ぶ。</p>
     */
    legalComplianceEnabled () {
      return Boolean(this.settings && this.settings.legal_compliance_enabled)
    },
    /**
     * 提出物の締めの言い方。
     *
     * **数え方と日が揃っていなければ空。**片方だけ入った状態を画面で
     * 補うと、設定した人は「入れたのに違う形で出る」ことになる。
     */
    submissionDueLabel () {
      const day = Number(this.settings.submission_due_day)
      if (!this.settings.submission_due_kind || !day) { return '' }
      return this.settings.submission_due_kind === 'BUSINESS_DAY'
        ? '第' + day + '営業日まで'
        : day + '日まで'
    },
    /**
     * 上限のお知らせが届いていないことの知らせ。届いていれば null。
     *
     * 言葉は utils に置いてある。専用画面「36協定アラート」でも
     * 同じ状態を出すので、片方だけ直すと黙ってずれる。
     */
    overtimeAlertNotice () {
      return overtimeAlertDeliveryNotice(
        this.settings.overtime_alert_delivery_blocker,
        this.settings.overtime_alert_uses_management_channel,
        this.settings.overtime_alert_last_failed_month
      )
    },
    /**
     * どこへ届くか。**空欄のままにしない。**
     *
     * 専用の通知先を決めていない会社は「—」だけが出て、届いていない
     * のか、どこかへ届いているのかが読めない。
     */
    overtimeAlertChannelLabel () {
      const dedicated = String(this.settings.overtime_alert_slack_channel || '').trim()
      if (dedicated) return dedicated
      return this.settings.overtime_alert_uses_management_channel
        ? '管理部門向け朝通知と同じチャンネル'
        : ''
    },
    /**
     * 時間外・休日労働時間の算定期日の言い方。
     *
     * **数え方と日が揃っていなければ空。**片方だけ入った状態を画面で
     * 補うと、設定した人は「入れたのに通知が来ない」ことになる。
     */
    overtimeCalculationDueLabel () {
      const day = Number(this.settings.overtime_calculation_due_day)
      if (!this.settings.overtime_calculation_due_kind || !day) { return '' }
      return this.settings.overtime_calculation_due_kind === 'BUSINESS_DAY'
        ? '第' + day + '営業日'
        : day + '日'
    },
    /**
     * いま効いている協定が、いつから効いているか。
     *
     * **空は「協定として記録されていない」。**法定の原則値で扱っている。
     */
    effectiveFromLabel () {
      return this.dayLabel(this.settings.overtime_agreement_valid_from)
    },
    /** いま効いている協定が、いつまでか。**空は「終わりが記録されていない」。** */
    validToLabel () {
      return this.dayLabel(this.settings.overtime_agreement_valid_to)
    },
    /** 対象期間の起算日。年の上限はこの日から1年で数える。 */
    annualStartLabel () {
      return this.dayLabel(this.settings.overtime_agreement_annual_start_date)
    },
    /**
     * 判定できているのか。**「問題なし」と同じ見た目にしない。**
     *
     * 協定が無い・切れている・内容が未登録の会社を、上限に触れていない
     * 会社と同じ顔で出すと、見ていないことが見えなくなる。
     */
    agreementStatusNotice () {
      return overtimeAgreementStatusNotice(
        this.settings.overtime_agreement_status,
        this.settings.overtime_agreement_status_message)
    },
    agreementStatusClasses () {
      return overtimeAgreementStatusClasses(this.agreementStatusNotice)
    },
    /** 協定の履歴。サーバが新しい順で返す。 */
    agreementHistory () {
      return Array.isArray(this.settings.overtime_agreement_history)
        ? this.settings.overtime_agreement_history
        : []
    },
    /**
     * この画面に出しているのが「今月の判定に使う協定」だと言う。
     *
     * **どの月の話かを書かないと、過去の月も同じ上限だと読まれる。**
     * それがまさに、協定を1組だけ持っていたときに起きていたこと。
     */
    agreementPeriodLabel () {
      return '下に出しているのは、今月の判定に使う協定です。'
        + '過去の月は、その月に効いていた協定で判定します。'
    },
    /** 36協定の対象期間の起算月。年の上限はここから1年で数える。 */
    startMonthLabel () {
      const month = Number(this.settings.overtime_agreement_start_month)
      return month >= 1 && month <= 12 ? month + '月' : '未設定'
    },
    /**
     * 限度時間を超えられる回数。
     *
     * **特別条項が無ければ0回。**サーバが返した「実際の回数」を出す。
     * ここで「特別条項が無ければ0」と書くと、判定側と二重に持つことになる。
     */
    exceedLabel () {
      const effective = this.settings.overtime_effective_exceed_month_count
      if (effective == null) { return '未設定' }
      const agreed = this.settings.overtime_agreement_exceed_month_count
      return Number(effective) === Number(agreed)
        ? `年${effective}回`
        : `年${effective}回（協定は${agreed}回）`
    },

    /**
     * 代休の期限を、1行で言い切る。
     *
     * **「あり」だけでは足りない。**何か月なのかが分からないと、
     * 本人にいつまでと伝えられない。逆に「なし」のときに月数を出すと、
     * 効いていない数字を見せることになる。
     */
    substituteExpiryText () {
      if (this.settings.substitute_leave_expiry_flg !== true) {
        return '設けない（代休は消滅しません）'
      }
      const months = this.settings.substitute_leave_expiry_months
      if (months == null) { return '設ける' }
      return `発生した月の末日から${months}か月`
    }
  },
  mounted () {
    this.loadSettings()
    this.loadInvoiceIssuer()
  },
  methods: {
    /**
     * 請求書記載情報を読む。
     *
     * **請求書を出せる契約（INVOICE）の会社だけ。**契約を確かめられなかったときは
     * 読まない＝カードを出さない（出す側へ倒すと、使われない欄が並ぶ）。
     * 読むのに失敗したときは、カードは出して、中に失敗を書く
     * （黙って消すと、欄が無くなったように見える）。
     */
    async loadInvoiceIssuer () {
      this.invoiceIssuer = null
      this.invoiceIssuerError = ''
      /*
       * 契約は、画面へ入るときに経路の守りが読み込んだものを見る
       * （ここで読みに行かない。読みに行くと、同じ問い合わせが二重になる）。
       */
      const context = getCachedAccessContext()
      const features = context && Array.isArray(context.features) ? context.features : []
      if (!features.includes('INVOICE')) return
      try {
        const result = await api.getInvoiceIssuer()
        this.invoiceIssuer = result && typeof result === 'object' ? result : {}
      } catch (error) {
        this.invoiceIssuer = {}
        this.invoiceIssuerError = '自社設定を取得できませんでした。'
      }
    },
    async loadSettings () {
      this.loading = true
      this.errorMessage = ''
      try {
        const result = await api.get()
        this.settings = Object.assign(emptySettings(), result || {})
        /*
         * 失敗していないのに月だけ残っていると、直したあとも帯が出続ける。
         * **フラグが立っているときだけ月を持つ。**
         */
        if (this.settings.overtime_alert_last_send_failed !== true) {
          this.settings.overtime_alert_last_failed_month = ''
        }
      } catch (error) {
        const detail = error && error.response && error.response.data &&
          (error.response.data.message)
        this.errorMessage = detail || '自社設定を取得できませんでした。'
      } finally {
        this.loading = false
      }
    },
    display (value) {
      return value == null || String(value).trim() === '' ? '未設定' : value
    },
    timeLabel (value) {
      return this.display(value)
    },
    // 「始業の30分前（08:30）」のように、決め方と結果を並べて出す。
    // 結果の時刻は**サーバが計算した値をそのまま**受け取って表示する。
    // どの時刻を出すかは呼び出し側で決める。分数から当てにいくと、
    // たまたま同じ分数のときに別の通知の時刻を出してしまう。
    leadLabel (minutes, baseLabel, time) {
      if (minutes == null) {
        return '未設定'
      }
      const suffix = time ? `（${time}）` : ''
      return Number(minutes) === 0
        ? `${baseLabel}ちょうど${suffix}`
        : `${baseLabel}の${minutes}分前${suffix}`
    },
    /** `yyyy-MM-dd` を読みやすく。**空は「記録されていません」。** */
    dayLabel (value) {
      const matched = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/)
      if (!matched) { return '記録されていません' }
      return matched[1] + '年' + Number(matched[2]) + '月'
        + Number(matched[3]) + '日'
    },
    /** 分を「45時間」の形にする。 */
    hoursLabel (minutes) {
      if (minutes == null || minutes === '') { return '未設定' }
      const value = Number(minutes)
      if (!Number.isFinite(value)) { return '未設定' }
      return (Math.round((value / 60) * 100) / 100) + '時間'
    },
    /**
     * 協定の値と、実際に当てはめる上限。
     *
     * **食い違うときだけ両方出す。**1年単位の変形労働時間制にチェックが
     * 入っているのに限度時間を直し忘れた会社は、ここで気づける。
     * どちらが当てはまるかを決めるのはサーバで、画面は受け取って並べるだけ。
     */
    limitLabel (agreedMinutes, effectiveMinutes) {
      const agreed = this.hoursLabel(agreedMinutes)
      const effective = this.hoursLabel(effectiveMinutes)
      if (effective === '未設定' || effective === agreed) { return agreed }
      return `${effective}（協定は${agreed}。法定の原則値で止めています）`
    },
    yesNo (value) {
      return value ? 'はい' : 'いいえ'
    },
    onOff (value) {
      return value ? 'オン' : 'オフ'
    },
    toggleColor (value) {
      return value ? 'success' : 'secondary'
    }
  }
}
</script>

<style scoped>
/*
  「設定済み」と値を、見た目で分ける。

  分けないと「設定済みhttps://…」と続いて読め、**値の一部に見える。**
  Vue は要素のあいだの改行を詰めるので、間隔は自分で作る。
*/
.calendar-set { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin: 0; }
.calendar-set__badge { display: inline-flex; align-items: center; flex: 0 0 auto; padding: 3px 10px; border-radius: 999px; background: #e9f4fb; color: #0a6ca8; font-size: 12px; font-weight: 700; white-space: nowrap; }
/* **長いURLは折り返す。**折り返さないと横に伸びて、カードごとはみ出す。 */
.calendar-set__url { min-width: 0; color: #36576b; font-size: 13px; line-height: 1.6; overflow-wrap: anywhere; word-break: break-all; }
.calendar-unset { margin: 0; color: #6a7f8c; font-size: 13px; }

@media (max-width: 575.98px) {
  /* 狭いときは、状態を上・値を下にして読みやすくする。 */
  .calendar-set { flex-direction: column; align-items: flex-start; gap: 6px; }
}

.tenant-setting-page { min-width: 0; padding: 24px; }
.page-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 24px; }
.page-header h1 { margin: 0 0 8px; font-weight: 800; }
.page-header p { max-width: 760px; margin: 0; color: #607884; line-height: 1.7; }
.page-header__action { flex: 0 0 auto; }
.eyebrow { margin: 0 0 4px; color: #0a6ca8; font-size: 12px; font-weight: 800; letter-spacing: .14em; }
.setting-loading { display: flex; align-items: center; justify-content: center; gap: 12px; min-height: 240px; color: #607884; }
.setting-card { min-width: 0; margin-bottom: 18px; }
/*
  **見出しの行を、カードの幅いっぱいに伸ばす。**
  伸ばさないと、右端へ寄せるはずの「変更」が
  見出しの文字の直後に付いてしまい、**カードごとに位置がずれる**
  （実測で 669〜804px までばらついた）。
*/
.section-heading { display: flex; align-items: flex-start; gap: 12px; width: 100%; }
.section-heading strong { color: #17384d; font-size: 16px; }
.section-heading p { margin: 3px 0 0; color: #6a7f8c; font-size: 12px; line-height: 1.6; }
.section-icon { display: inline-flex; width: 38px; height: 38px; flex: 0 0 auto; align-items: center; justify-content: center; border-radius: 12px; background: #e9f4fb; color: #0a6ca8; }
.setting-summary-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin: 0; }
.setting-summary-grid--wide { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.setting-summary-grid > div { min-width: 0; padding: 14px; border: 1px solid #dfe8ee; border-radius: 12px; background: #f8fbfd; }
.setting-summary-grid dt { margin-bottom: 6px; color: #617887; font-size: 12px; font-weight: 800; }
.setting-summary-grid dd { min-width: 0; margin: 0; color: #17384d; font-size: 14px; font-weight: 700; overflow-wrap: anywhere; }
.slack-status-row { display: flex; align-items: center; justify-content: space-between; gap: 14px; margin-bottom: 14px; padding: 14px; border: 1px solid #dce8ef; border-radius: 12px; background: #f6fafc; }
.slack-status-row > div { display: grid; gap: 3px; }
.status-label { color: #617887; font-size: 12px; font-weight: 800; }
.notification-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
.notification-card { min-width: 0; padding: 16px; border: 1px solid #dce8ef; border-radius: 14px; background: #fff; }
/*
 * 「届いていません」の帯。**エラーの赤にしない。**画面は正しく動いていて、
 * 直すのは設定のほう。赤にすると壊れていると読まれ、読み飛ばされる。
 */
.delivery-notice { display: grid; gap: 4px; margin-bottom: 12px; padding: 12px; border: 1px solid #e8d3a6; border-radius: 10px; background: #fffaf0; }
.delivery-notice strong { color: #7c5510; font-size: 13px; }
.delivery-notice small { color: #7f6a49; font-size: 12px; line-height: 1.7; }
/* 届いてはいるが、届く場所が違う。**注意ではなく案内。** */
.delivery-notice--info { border-color: #cfe1ef; background: #f6fbff; }
.delivery-notice--info strong { color: #1d4a6c; }
.delivery-notice--info small { color: #4a6b81; }
.notification-card__header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 14px; }
.notification-card h3 { margin: 0 0 5px; color: #17384d; font-size: 15px; }
.notification-card p { margin: 0; color: #6a7f8c; font-size: 12px; line-height: 1.6; }
.notification-card dl { display: grid; gap: 8px; margin: 0; }
.notification-card dl > div { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding-top: 8px; border-top: 1px solid #edf2f5; }
.notification-card dt { color: #617887; font-size: 12px; }
.notification-card dd { min-width: 0; margin: 0; color: #17384d; font-weight: 700; text-align: right; overflow-wrap: anywhere; }
.setting-note { margin: 0 0 12px; color: #6a7f8c; font-size: 12px; line-height: 1.7; }
/* 協定の履歴。狭い画面では表だけ横に流す（ページ全体は横に流さない）。 */
/*
 * 判定できているか。**7つの状態が、どれも同じ見た目にならないように。**
 * 色（--tone）で大きく分け、状態そのもの（--is-*）で左の線を変える。
 * 同じ強さなら後の規則が勝つので、状態のほうを後ろへ置く。
 */
.agreement-status { display: flex; align-items: flex-start; gap: 12px; margin: 0 0 14px; padding: 14px; border: 1px solid #dce8ef; border-left: 6px solid #9fb4c1; border-radius: 12px; background: #f8fbfd; }
.agreement-status__badge { flex: 0 0 auto; padding: 4px 10px; border-radius: 999px; background: #9fb4c1; color: #fff; font-size: 12px; font-weight: 800; white-space: nowrap; }
.agreement-status__body { display: grid; min-width: 0; gap: 4px; }
.agreement-status__body strong { color: #17384d; font-size: 14px; }
.agreement-status__body small { margin: 0; color: #56707f; font-size: 12px; line-height: 1.7; }
.agreement-status__action { font-weight: 700; }
.agreement-status--ok { border-color: #bfe0c6; border-left-color: #2f8a4e; background: #f4fbf6; }
.agreement-status--ok .agreement-status__badge { background: #2f8a4e; }
.agreement-status--warning { border-color: #e8d3a6; border-left-color: #c07f12; background: #fffaf0; }
.agreement-status--warning .agreement-status__badge { background: #c07f12; }
.agreement-status--danger { border-color: #efc4c9; border-left-color: #c93442; background: #fdf5f6; }
.agreement-status--danger .agreement-status__badge { background: #c93442; }
.agreement-status--blocked { border-color: #d3ccea; border-left-color: #6a4bb0; background: #f8f6fd; }
.agreement-status--blocked .agreement-status__badge { background: #6a4bb0; }
.agreement-status--unknown { border-color: #cfd8de; border-left-color: #5b6b76; background: #f6f8f9; }
.agreement-status--unknown .agreement-status__badge { background: #5b6b76; }
.agreement-status--is-valid { border-left-width: 6px; }
.agreement-status--is-no_agreement { border-left-style: solid; border-left-width: 10px; }
.agreement-status--is-expired { border-left-style: double; border-left-width: 10px; }
.agreement-status--is-unconfirmed_system_default { border-left-style: dashed; border-left-width: 8px; }
.agreement-status--is-unsupported_non_month_start { border-left-style: solid; border-left-width: 10px; }
.agreement-status--is-overlapping_agreements { border-left-style: double; border-left-width: 10px; }
.agreement-status--is-insufficient_worktime_data { border-left-style: dotted; border-left-width: 10px; }
.agreement-status--is-unknown { border-left-style: dashed; border-left-width: 6px; }
.setting-agreement-history { margin-top: 16px; overflow-x: auto; }
.setting-agreement-history h6 { margin: 0 0 8px; color: #17384d; font-size: 13px; font-weight: 800; }
.setting-agreement-history table { width: 100%; min-width: 560px; border-collapse: collapse; font-size: 13px; }
.setting-agreement-history th { padding: 8px 10px; border-bottom: 2px solid #dfe8ee; color: #617887; font-size: 12px; font-weight: 800; text-align: left; white-space: nowrap; }
.setting-agreement-history td { padding: 8px 10px; border-bottom: 1px solid #edf2f5; color: #17384d; white-space: nowrap; }
.setting-agreement-history .badge { margin-left: 6px; }
.setting-agreement-history .setting-note { margin: 10px 0 0; }
.bottom-action { display: flex; justify-content: center; margin-top: 24px; }
.bottom-action .btn { min-width: 180px; }

@media (max-width: 1199.98px) {
  .notification-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 991.98px) {
  .setting-summary-grid,
  .setting-summary-grid--wide { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 767.98px) {
  .tenant-setting-page { padding: 12px 0; }
  .page-header { flex-direction: column; gap: 16px; margin-bottom: 16px; }
  .page-header h1 { font-size: 28px; line-height: 1.25; }
  .page-header__action,
  .page-header__action .btn,
  .bottom-action,
  .bottom-action a,
  .bottom-action .btn { width: 100%; }
  .page-header__action .btn,
  .bottom-action .btn { min-height: 44px; }
  .setting-summary-grid,
  .setting-summary-grid--wide,
  .notification-grid { grid-template-columns: minmax(0, 1fr); }
  .section-heading p { overflow-wrap: anywhere; }
  .slack-status-row,
  .notification-card__header { align-items: flex-start; flex-wrap: wrap; }
  .notification-card__header .badge { flex: 0 0 auto; }
  .notification-card dl > div { display: grid; grid-template-columns: minmax(110px, 42%) minmax(0, 1fr); }
  /* 帯は縦に。**札や文言を切らない。** */
  .agreement-status { flex-direction: column; }
}
</style>
